import { Button, Text } from "@mantine/core";
import { useState } from "react";
import { FieldDeposit } from "./types.ts";
import { computeUpdatedStability, numberFormat } from "./utils.ts";
import style from "./AsteroidField.module.css";

const stabilityDescriptions: Record<string, string> = {
  stable: ">6 mo",
  unsettled: "<6 mo",
  unsteady: "<3 mo",
  volatile: "<1 mo",
  expired: ''
};

interface DepositSummaryRowProps {
  name: string;
  deposits: FieldDeposit[];
  lastFullImportAt: number | null;
}

export default function DepositSummaryRow({ deposits, name, lastFullImportAt }: DepositSummaryRowProps) {
  const [visible, setVisible] = useState(false);
  const total = deposits.reduce((sum, item) => sum + item.size, 0);

  return (
    <>
      <tr>
        <td className="capitalize">{name}</td>
        <td>{numberFormat.format(total)}</td>
        <td className={style.center}>{deposits.length}</td>
        <td>
          <Button variant="subtle" size="compact-xs" onClick={() => setVisible((v) => !v)}>
            {visible ? "Hide" : "Show"} details
          </Button>
        </td>
      </tr>
      {visible ? (
        <tr>
          <td colSpan={4}>
            <table className={style.table}>
              <thead>
                <tr>
                  <th>Location</th>
                  <th>Size</th>
                  <th>Original stability</th>
                  <th>Updated stability</th>
                </tr>
              </thead>
              <tbody>
                {deposits.map((deposit) => {
                  const updated = computeUpdatedStability(deposit.stability, lastFullImportAt);
                  return (
                    <tr
                      key={`${deposit.x}-${deposit.y}-${deposit.type}-${deposit.size}`}
                      className={updated.shouldBeExpired ? style.expiredRow : undefined}
                    >
                      <td>
                        ({deposit.x},{deposit.y})
                      </td>
                      <td>{numberFormat.format(deposit.size)}</td>
                      <td>
                        <span className="capitalize">{deposit.stability ?? "Unknown"}</span> (
                        {stabilityDescriptions[deposit.stability?.toLowerCase() ?? ""] ?? ""})
                      </td>
                      <td>
                        <Text tt="capitalize">{updated.status}</Text>
                        {updated.status !== 'expired' && `(${stabilityDescriptions[updated.status.toLowerCase()]})`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </td>
        </tr>
      ) : null}
    </>
  );
}
